/**
 * Engine-aware SQL emit for the `spatial` filter operator (bbox + distance).
 *
 * SHARED between the renderer-side `filterWhereBuilder.ts` and the main-process
 * `electron/table-explorer/queryBuilder/queryUtils.ts` so the Query Editor /
 * Data-Profile paths and the live Table Explorer produce BYTE-IDENTICAL spatial
 * predicates (A1/CD1+G11). Dependency-clean (type-only imports) — safe to
 * import from electron main, mirroring `jsonFilterSql.ts`.
 *
 * Every wire form is pinned by the SPATIAL-FACTS appendix (a6/b4/c4/d2/d3),
 * captured from live engines by T0 — NOT guessed. `quotedField` (and, for T19
 * declared pairs, `opts.quotedLngField`) arrive ALREADY quoted; this module
 * only assembles predicates from NUMBERS + fixed keywords (no string
 * interpolation of user text). Unsupported engine / kind / SRID combinations
 * throw a tagged error; the chip UI hides those options via
 * `canEmitSpatialSubOperator` so the throw is a defense-in-depth backstop.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type {
  FilterCondition,
  SpatialFilterContext,
  SpatialSubOperator,
} from '../../core/types/workspaceTypes';
import {
  buildBboxSql,
  buildDistanceSql,
  makeSpatialFilterUnsupportedError,
  isSpatialFilterUnsupportedError,
} from './spatialFilterEmit';
import { buildPolygonSql } from './spatialFilterPolygonEmit';

export { makeSpatialFilterUnsupportedError, isSpatialFilterUnsupportedError };

/**
 * Whether the verified forms can emit `subOp` for `engine` + this geometry
 * kind / SRID. The chip UI uses this to hide unsupported options (with a
 * tooltip explaining why); the emitter throws for the same cases.
 */
export function canEmitSpatialSubOperator(
  engine: DatabaseEngineId,
  subOp: SpatialSubOperator,
  ctx: SpatialFilterContext | undefined,
): boolean {
  const srid = ctx?.srid ?? null;
  const kind = ctx?.kind ?? 'geometry';
  if (kind === 'declared-pair') return subOp === 'intersects_bbox' && !!ctx?.lngColumn;
  switch (engine) {
    case 'pgsql':
      // bbox + polygon reproject the 4326 shape per-row via ST_Transform(ST_SRID(g))
      // (geography builds a 4326 geography shape directly), so they are SRID-agnostic.
      // distance casts ::geography (PostGIS geography = SRID 4326) — offered only on a
      // geographic column.
      if (subOp === 'intersects_bbox' || subOp === 'intersects_polygon') return true;
      return kind === 'geography' || srid === 4326;
    case 'mysql':
      // b4 emits a 4326 envelope + axis-order=long-lat; MBRIntersects and
      // ST_Distance_Sphere both require a SRID-4326 column.
      return srid === 4326;
    case 'oracle':
      // d2/d3 use tolerance 0.05 for geodetic 4326 — only 4326 is verified.
      return srid === 4326;
    case 'mssql':
      // The emit embeds the column SRID — a known (non-null) SRID is required.
      return srid !== null;
    case 'sqlite':
      // Only the MbrIntersects/BuildMbr bbox form is verified; no distance form.
      return subOp === 'intersects_bbox';
    default:
      return false; // kusto / mock — no spatial SQL
  }
}

/**
 * Build a single spatial WHERE fragment. Returns `''` when the chip is
 * incomplete (no bbox / no point+distance yet) so a half-built chip is inactive
 * rather than an error; throws `makeSpatialFilterUnsupportedError` when the
 * engine / kind / SRID combination has no verified form.
 */
export function buildSpatialConditionSql(
  quotedField: string,
  condition: FilterCondition,
  engine: DatabaseEngineId,
  opts?: { quotedLngField?: string },
): string {
  const ctx: SpatialFilterContext = condition.spatialContext ?? { kind: 'geometry', srid: null };
  const subOp = condition.spatialSubOperator;

  if (subOp === 'within_distance') {
    if (!canEmitSpatialSubOperator(engine, subOp, ctx)) throw makeSpatialFilterUnsupportedError(engine);
    if (!condition.point || typeof condition.distanceMeters !== 'number') return '';
    return buildDistanceSql(quotedField, ctx, condition.point, condition.distanceMeters, engine);
  }

  if (subOp === 'intersects_polygon') {
    // Explicit branch (never falls through to bbox — a silent bbox emit would be a WRONG result).
    if (!canEmitSpatialSubOperator(engine, subOp, ctx)) throw makeSpatialFilterUnsupportedError(engine);
    if (!condition.polygon || condition.polygon.length < 3) return ''; // half-drawn ⇒ inactive
    return buildPolygonSql(quotedField, ctx, condition.polygon, engine);
  }

  // Default / intersects_bbox (also the `subOp === undefined` legacy path).
  if (!canEmitSpatialSubOperator(engine, 'intersects_bbox', ctx)) throw makeSpatialFilterUnsupportedError(engine);
  if (!condition.bbox) return '';
  return buildBboxSql(quotedField, ctx, condition.bbox, engine, opts?.quotedLngField);
}
